import type {IssueFieldMultiSelectOption} from '@github-ui/item-picker/IssueMultiSelectFieldPicker'
import {IssueMultiSelectFieldPicker} from '@github-ui/item-picker/IssueMultiSelectFieldPicker'
import {AlertIcon, GlobeIcon} from '@primer/octicons-react'
import {Tooltip} from '@primer/react'
import {clsx} from 'clsx'
import {type ComponentPropsWithoutRef, type Ref, useCallback, useId} from 'react'

import {LABELS} from './constants/labels'
import fieldItemStyles from './IssueFieldItem.module.css'
import {IssueFieldMultiSelectValueTokens} from './IssueFieldSingleSelectValueToken'
import styles from './IssueFieldSingleSelectValueToken.module.css'
import {TooltipTriggerWrapper} from './utils/TooltipTriggerWrapper'

export type MultiSelectInitialValue = IssueFieldMultiSelectOption[] | null
export type MultiSelectOnCommit = (fieldId: string, value: IssueFieldMultiSelectOption[] | null) => void

export type IssueFieldMultiSelectEditorProps = {
  fieldId: string
  fieldName: string
  readonly: boolean
  initialValue: MultiSelectInitialValue
  onCommit: MultiSelectOnCommit
  /// If set to true, then the multi select editor should render in open state
  shouldOpen?: boolean
  isDraft?: boolean
  className?: string
  errorPresent?: boolean
  showVisibilityIndicator?: boolean
}

type IssueFieldMultiSelectEditorButtonProps = {
  anchorProps: ComponentPropsWithoutRef<'button'>
  buttonRef: Ref<HTMLButtonElement> | undefined
  ariaLabel: string
  fieldName: string
  displayValue: React.ReactNode
  isNoValue: boolean
  className?: string
  errorPresent: boolean
  showVisibilityIndicator: boolean
  visibilityTooltipId?: string
}

const IssueFieldMultiSelectEditorButton = ({
  anchorProps,
  buttonRef,
  ariaLabel,
  fieldName,
  displayValue,
  isNoValue,
  className,
  errorPresent,
  showVisibilityIndicator,
  visibilityTooltipId,
}: IssueFieldMultiSelectEditorButtonProps) => {
  const content = (
    <button
      ref={buttonRef}
      className={clsx(fieldItemStyles.issueFieldFormWrapper, fieldItemStyles.issueFieldFormWrapperClickable, className)}
      aria-label={ariaLabel}
      {...anchorProps}
      aria-describedby={visibilityTooltipId}
    >
      <span className={fieldItemStyles.issueFieldLabel}>
        {fieldName}
        {showVisibilityIndicator && (
          <span className={fieldItemStyles.visibilityIconWrapper}>
            <GlobeIcon size={12} />
          </span>
        )}
      </span>
      <span
        className={clsx(
          fieldItemStyles.issueFieldValueText,
          !isNoValue && fieldItemStyles.hasValue,
          styles.compactValueContainer,
        )}
      >
        {displayValue}
      </span>
      {errorPresent && (
        <span className={fieldItemStyles.alertIconWrapper}>
          <AlertIcon className={fieldItemStyles.alertIcon} />
        </span>
      )}
    </button>
  )

  return errorPresent ? (
    <Tooltip text={LABELS.errorUpdatingIssueField} type="description">
      {content}
    </Tooltip>
  ) : (
    content
  )
}

export const IssueFieldMultiSelectEditor = ({
  fieldId,
  fieldName,
  readonly,
  initialValue,
  onCommit,
  shouldOpen = false,
  isDraft = false,
  className,
  errorPresent = false,
  showVisibilityIndicator = false,
}: IssueFieldMultiSelectEditorProps) => {
  const visibilityTooltipId = useId()

  const onSelectionChanged = useCallback(
    (selectedOptions: IssueFieldMultiSelectOption[]) => {
      onCommit(fieldId, selectedOptions.length > 0 ? selectedOptions : null)
    },
    [fieldId, onCommit],
  )

  const onClose = useCallback(() => {
    if (isDraft) {
      onCommit(fieldId, null)
    }
  }, [fieldId, isDraft, onCommit])

  const isNoValue = !initialValue || initialValue.length === 0

  const displayValue = isNoValue ? (
    LABELS.emptyValueLabels.multiSelect
  ) : (
    <IssueFieldMultiSelectValueTokens
      options={initialValue.map(option => ({id: option.id, name: option.name, color: option.color}))}
      getTooltipText={() => undefined}
      size="small"
      containerClassName={styles.compactContainer}
    />
  )

  const ariaLabel = isNoValue ? `Set ${fieldName}` : `Edit ${fieldName}`

  if (readonly) {
    return (
      <div className={clsx(fieldItemStyles.issueFieldFormWrapper, className)}>
        <p className={fieldItemStyles.issueFieldLabel}>{fieldName}</p>
        <div
          className={clsx(
            fieldItemStyles.issueFieldValueText,
            !isNoValue && fieldItemStyles.hasValue,
            styles.compactValueContainer,
          )}
        >
          {isNoValue ? LABELS.setValue : displayValue}
        </div>
      </div>
    )
  }

  return (
    <IssueMultiSelectFieldPicker
      fieldId={fieldId}
      fieldName={fieldName}
      selectedOptions={initialValue}
      onSelectionChange={onSelectionChanged}
      anchorElement={(anchorProps, ref) => {
        const anchor = (
          <IssueFieldMultiSelectEditorButton
            anchorProps={anchorProps}
            buttonRef={ref}
            ariaLabel={ariaLabel}
            fieldName={fieldName}
            displayValue={displayValue}
            isNoValue={isNoValue}
            className={className}
            errorPresent={errorPresent}
            showVisibilityIndicator={showVisibilityIndicator}
            visibilityTooltipId={showVisibilityIndicator ? visibilityTooltipId : undefined}
          />
        )
        return showVisibilityIndicator ? (
          <Tooltip
            id={visibilityTooltipId}
            text={LABELS.visibilityIndicatorTooltip}
            type="description"
            direction="e"
            delay="medium"
          >
            <TooltipTriggerWrapper>{anchor}</TooltipTriggerWrapper>
          </Tooltip>
        ) : (
          anchor
        )
      }}
      readonly={false}
      isLazy
      onClose={onClose}
      shouldOpen={shouldOpen}
    />
  )
}
